/* =============================================================================
   hd-align.js —— 智能参考线 + 吸附（2026-09-14 批次4b）/ 方向键微调

   职责：
     · 拖动时在画布上绘制**智能参考线**（洋红虚线），并让被拖对象自动吸附到
       打印区 / 其它对象的左中右·上中下特征线
     · 方向键微调（←↑→↓ 1px，Shift 组合 10px）

   ⚠️ 2026-09-15：原「批次4a 对齐面板」（6 对齐 / 2 分布 / 居中到打印区 / X·Y·W·H·角度
      数值框）**已整体删除** —— 用户判定它与吸附功能重复。对应样式表与 board 上
      的空容器一并移除。**不要复活。**

   ✅ 2026-09-15 晚 修两项（用户报「参考线亮了、松手后中心线却不重叠」）：
      A. 容差单位 —— 原先 6/12 被当作**画布逻辑坐标**用 ⇒ 实际仅 1.62 / 3.18 屏幕px
         （画布 3000² 而显示区 ~800²，缩放只在 CSS 层）⇒ 手指几乎吸不住。
         现按 `_pfmDisplayScale` 反算：鼠标 6 屏幕px / 手指 12 屏幕px。
      B. 匹配优先级 —— 新增 `pickAxis()`：先试「中心↔中心」，不命中才退到全体特征。
         避免「左边缘吸到中心线」⇒ 参考线亮了却不穿过对象中心。

   ✅ 2026-09-15 晚（第二批）：
      A′. 容差上调为 **鼠标 8 / 手指 18 屏幕px**（原 6 / 12）—— 用户实测「差 14.5px
          就吸不住，看着没居中」。两级梯度：拖动中 ≤18px 实时吸附 + 显示参考线；
          松手瞬间 ≤**30px**（`SNAP_SCREEN`）由 `snapToCenter()` 兜底贴打印区中心。
      A‴. **几何基准改用「内容边界」**（用户报「吸附的线和中心线不对齐」）：
          实测图片矩形精确居中后**可见图案**仍偏 17 屏幕px（PNG 上下透明边距不均）。
          `boxOf()` 现优先取 `window.__pfmContentBox(obj)`（主 IIFE 提供 · alpha 扫描 + 缓存），
          非图片/旋转/原语缺失时回退 `getBoundingRect` ⇒ 对齐/参考线都按**看得见的内容**算。
      A″. **回调**（用户报「吸附太强了，根本无法拖动」）—— 容差 **6 / 10 屏幕px**、
          磁吸窗口 **8 屏幕px**。依据 `probe_snap_density.js` 实测：容差 18px 时拖动路径上
          **56.4%** 的位置会被吸走（大图 1117px + 6 条参照线），超过一半 ⇒ 拖不动。
          且**边缘匹配(33.3%) 贡献大于中心匹配(23.1%)** —— 干扰主源是「边↔线」。
          回调后覆盖率 ≈ 37%（手指 10px）/ 25%（鼠标 6px + 仅打印区时 13.5%）。
      D. 松手磁吸**只针对打印区中心**（不吸其它对象），X/Y 轴独立判断，可撤销（走 saveState）。
          磁吸后把中心参考线**闪 260ms** 作反馈 —— 否则用户会觉得「我放这儿它自己跳了」。
          ⚠️ 判断必须用「拖动最后一帧、**吸附前**的意图位置」（`_lastCx/_lastCy`）：
             边缘吸附会把对象中心推离用户意图（对象越宽推得越远 —— 实测平板宽对象被推到
             半宽 33.5 屏幕px 处，已超出 30px 窗口）⇒ 拿吸附后的中心判断会让磁吸**永远够不着**。
             依据：fabric 的 drag 用**指针增量**算 left/top，与我们对 left/top 的修正无关，
             故 `object:moving` 里读到的 bb 就是用户的意图位置。
      ⚠️ 待办：B（吸附按图片**实际内容边界**）并入「全设计板内容边界」批次一起做，
         此处**不重复实现**（那份清单含 5 处：boxOf / checkPrintBounds /
         normalizeOffcanvasObjects / addImage / pfmFitToPrintArea）。

   ✅ 2026-09-19 吸附减弱（用户「自动吸附太强」→ 方案 C）：
      A. 容差 10/6 → **7/4 屏幕px**（`tolScreen()`）
      B. 参照物**只保留打印区**，不再吸画布上其它对象（`buildRefs()` + 常量 `SNAP_OTHERS`）
      实测（`probe_snap_density.js` · 改前）：横向命中率 **51.8%**、纵向 **46.3%**，
      靠近另一对象时高达 **96.2%** —— 拖动路径上一半以上位置会被吸走。
      放大机制：画布 3000² 而显示 `_pfmDisplayScale≈0.122` ⇒ 10 屏幕px ≈ **82 逻辑px** 容差。

   设计约束（沿用同族规范）：
     · 面板**常驻不隐藏**、不可用时置灰 —— 面板高度恒定，不跳动
     · 所有操作走主 IIFE 暴露的公共原语（`__pfmSelectedObjs` / `__pfmSaveState`），
       不在此文件内重复实现「取选中集合」
     · 小图标一律内联 SVG（字符图标落基线下方，视觉不居中 —— 已踩过）

   移动端：手指拖不到精确位置 → 对齐/分布/居中是移动端最主要的入口。
   ============================================================================= */
(function () {
  'use strict';

  var D = null;          // deps（由主 IIFE 注入）
  var ready = false;     // 面板是否已渲染
  var bound = false;     // fabric 事件是否已绑定（可补绑，见 bindCanvasEvents）
  var keyBound = false;  // 键盘监听是否已绑定

  function q(id) { return document.getElementById(id); }

  function CV() {
    try { if (D && D.canvas) return D.canvas() || null; } catch (e) { }
    try { return window.__pfmCanvas || null; } catch (e) { }
    return null;
  }

  /* ---------------------------------------------------------------------------
     选中集合 —— 一律走主 IIFE 的公共原语，避免出现第二份实现。
     fallback 只为「原语尚未就绪」的极端时序兜底，逻辑与公共原语保持一致。
     --------------------------------------------------------------------------- */
  function selObjs() {
    try { if (window.__pfmSelectedObjs) return window.__pfmSelectedObjs() || []; } catch (e) { }
    var c = CV(), out = [];
    if (!c) return out;
    try {
      if (c.getActiveObjects) {
        c.getActiveObjects().forEach(function (o) { if (o) out.push(o); });
      }
    } catch (e) { }
    try {
      var a = c.getActiveObject && c.getActiveObject();
      if (a && a.type !== 'activeSelection' && out.indexOf(a) < 0) out.push(a);
    } catch (e) { }
    return out;
  }

  /* 当前打印区（矩形）。canvas._pfmPrintArea 是加载产品时写入的、与当前面同步的一份，
     优先用它；取不到再回退到 printAreaSpecs。 */
  function printRect() {
    var c = CV();
    var pa = c && c._pfmPrintArea;
    if (pa && pa.width > 0 && pa.height > 0) {
      return { left: pa.left, top: pa.top, width: pa.width, height: pa.height };
    }
    try {
      var specs = window.__pfmPrintArea && window.__pfmPrintArea();
      if (!specs) return null;
      var cur = window.__pfmGetPlacement && window.__pfmGetPlacement();
      var s = specs[cur] || specs['front'] || Object.values(specs)[0];
      if (s && s.width > 0) return { left: s.left, top: s.top, width: s.width, height: s.height };
    } catch (e) { }
    return null;
  }

  /* 对象在画布坐标系下的包围盒（true,true = 含描边/旋转的绝对坐标）
     ✅ 2026-09-15 批次A：**优先用「内容边界」**（`window.__pfmContentBox`，由主 IIFE 提供）。
        PNG 常有四周透明留白 —— 按矩形对齐时「矩形精确居中了、看得见的图案却仍偏」
        （实测上下边距不均的图偏 17 屏幕px），用户会当成「吸附没对准中心」。
        本函数是吸附的**唯一几何入口**，它同时供匹配特征线、_lastCx/_lastCy 意图位置、
        以及 buildRefs 里其它对象的参照线 ⇒ 改这一处三处一起正确。
     回退链：原语不可用 / 返回空 / 非图片 / 旋转对象 → 一律回退矩形（与旧行为一致）。 */
  function boxOf(o) {
    try {
      if (window.__pfmContentBox) {
        var cb = window.__pfmContentBox(o);
        if (cb && cb.width >= 0) return cb;
      }
    } catch (e) { }
    try {
      var r = o.getBoundingRect(true, true);
      return (r && r.width >= 0) ? r : null;
    } catch (e) { return null; }
  }

  /* 平移对象。originX/originY 不影响「位移量 = left/top 变化量」这一等价关系，
     所以无需按 origin 换算。 */
  function moveBy(o, dx, dy) {
    if (!dx && !dy) return;
    try {
      o.set({ left: (o.left || 0) + dx, top: (o.top || 0) + dy });
      if (o.setCoords) o.setCoords();
    } catch (e) { }
  }

  /* 提交：同步 activeSelection 的包围框 → 渲染 → 入撤销栈（方向键微调用） */
  function commit() {
    var c = CV(); if (!c) return;
    try {
      var ao = c.getActiveObject && c.getActiveObject();
      if (ao && ao.type === 'activeSelection' && ao.setCoords) ao.setCoords();
    } catch (e) { }
    try { c.renderAll(); } catch (e) { }
    try { if (D && D.save) D.save(); } catch (e) { }
  }

  /* ---------------------------------------------------------------------------
     智能参考线 + 吸附（批次4b）

     设计要点：
       · 参考线**不创建 fabric 对象** —— 在 `after:render` 里直接画到画布上下文。
         好处：不进图层列表、不进撤销栈、不必担心 z-order；代价是要自己处理
         viewportTransform。**导出安全性**由 `_moving` 标志兜住：导出时不在拖动。
       · 参照物（其他对象 + 打印区）包围盒在**拖动开始时算一次并缓存**，拖动期间复用
         —— 拖动中它们不会移动。每帧只算「被拖对象」一个包围盒，不拖慢拖动。
       · 容差按 **pointer:coarse** 判断，不用宽度：鸿蒙平板竖屏宽 >768 但仍是粗指针，
         手指精度低于鼠标，容差需从 6px 放大到 12px。
     --------------------------------------------------------------------------- */
  var _moving = false;      // 正在拖动 → 决定是否绘制参考线
  var _guides = [];         // [{ vertical:bool, pos:number }]
  var _refX = null;         // 参照物 X 候选（打印区 + 其他对象）
  var _refY = null;         // 参照物 Y 候选
  var _refCx = null;        // 上面两者中**仅中心线**那一份 —— 「中心↔中心」优先级更高（见 pickAxis）
  var _refCy = null;
  var _refBoxes = null;     // 参照物包围盒（等间距检测用；与 _refX 同批缓存、同批失效）
  var _spacings = [];       // 本轮命中的等间距提示 [{axis,a1,b1,a2,b2,at,gap}]
  /* 拖动**每一帧**记录被拖对象在「吸附前」的中心 = 用户指针的**意图位置**。
     松手磁吸必须用它判断 —— 不能拿当前中心：边缘吸附会把中心推离（对象越宽推得越远），
     导致「明明拖到了中心附近，磁吸窗口却够不着」。见 snapToCenter()。 */
  var _lastCx = null;
  var _lastCy = null;

  function coarsePointer() {
    try { return !!(window.matchMedia && window.matchMedia('(pointer:coarse)').matches); } catch (e) { return false; }
  }
  /* 容差以**屏幕像素**为设计意图（鼠标 2.4 / 手指 1.2），再按显示缩放反算成画布逻辑坐标。
     ⚠️ 2026-09-15 修正史（两步）：
       · 原实现直接把 6/12 当成画布逻辑值参与比较 —— 画布位图 3000² 而显示区仅 ~800²，
         这层缩放发生在 **CSS 上**（`getZoom()` 恒为 1）⇒ 容差被缩小约 3.8 倍
         （平板 12 → 实际 3.18 屏幕px；桌面 6 → 1.62 屏幕px），手指几乎吸不住。
       · 反算修好后 6/12 仍偏紧：用户实测「Y 只差 14.5px 就吸不住，看着没居中」
         ⇒ 上调为 8 / 18，但**实测覆盖率过高**（容差 18px 时拖动中 56.4% 的位置会被吸走
         ⇒ 根本无法拖动）⇒ 最终**回调为 6 / 10**（详见文件头 A″ 段），磁吸窗口同步 30 → 8。
     同族做法见 pfmCornerLogical() / drawGrid() 的线宽公式。 */
  /* 2026-09-19: 吸附强度按「目标命中率」校准（用户：「自动吸附太强」→「还是太黏」→「命中率改为 5%」）。
     手指 10→7→4→3.4→**1.2**、鼠标 6→4→3→2.4→0.8→**2.4（当前）**（屏幕px，容许小数）。
     真机扫描曲线（`probe_tol_curve.js`：真实引擎 + 临时改 `_pfmDisplayScale` 等效模拟，对象 200×150）：
       3.4→**15.4%** ｜ 2.5→11% ｜ 2.0→9.1% ｜ 1.5→6.7% ｜ 1.2→5.1% ｜ 1.0→4.4% ｜ 0.8→3.5%
     ✅ 该曲线全程**吸附位置数恒为 9**（3 条打印区线 × 3 种对齐）⇒ 本次**只调容差、未动功能结构**。
     ⚠️ 1.2 屏幕px ≈ 10 逻辑px ⇒ 手指要拖到目标约 1px 内才吸 ⇒ 手感是「几乎不粘、偶尔嗒一下」。
     🔴 2026-10-06（用户裁决 甲）：**鼠标 0.8 → 2.4**（命中率 ≈11%）。理由：电脑端 0.8 屏幕px
        **小于鼠标最小可分辨位移 1px** ⇒ 理论上永远跳不进窗口，只能靠运气（用户：「电脑板吸附太难对准」）。
        手指保持 1.2（宽屏端实测「还可以」）。想回调：把下面的 1.2 / 2.4 改回 1.2 / 0.8（旧「几乎不粘」手感）。 */
  function tolScreen() { return coarsePointer() ? 1.2 : 2.4; }
  function guideTol() {
    var c = CV();
    var sc = Math.max(0.001, (c && c._pfmDisplayScale) || 1);
    return tolScreen() / sc;
  }

  /* 在 selfVals × refVals 中找最近的一对；返回需平移的增量与参照位置 */
  function nearest(selfVals, refVals, t) {
    var best = null;
    for (var i = 0; i < selfVals.length; i++) {
      for (var j = 0; j < refVals.length; j++) {
        var d = refVals[j] - selfVals[i];
        if (Math.abs(d) <= t && (!best || Math.abs(d) < Math.abs(best.delta))) {
          best = { delta: d, pos: refVals[j] };
        }
      }
    }
    return best;
  }

  /* 单轴匹配，两级优先级（2026-09-19 曾一度简化为「只做中心对齐」，**当日经用户要求恢复**）：
       ① **中心 ↔ 中心** —— 拖图案时的默认意图就是「摆正 / 居中」，先只拿对象中心去撞
          参照中心线，命中即用（主流设计软件的默认手感）
       ② ① 不命中才退到**全体特征**（对象 左/中/右 × 参照 左/中/右，取最近一对）
     ⚠️ ① 同样受容差约束，所以「中心差很远、但边缘刚好对齐」仍会走 ② ——
        边缘对齐能力**没有被废掉**，只是两者同时可用时中心优先。 */
  function pickAxis(selfVals, selfCenter, refVals, refCenters, t) {
    var hit = nearest([selfCenter], refCenters, t);
    return hit || nearest(selfVals, refVals, t);
  }

  /* 2026-09-19: 用户「吸附太强」→ 方案 C 的 B 部分。
     false = 只把「打印区」当参照物（不再吸画布上其它对象）；true = 旧行为（也吸其它对象）。 */
  var SNAP_OTHERS = false;
  function buildRefs(target) {
    var c = CV(); if (!c) return;
    var rx = [], ry = [], cx = [], cy = [], boxes = [];
    var pa = printRect();
    if (pa) {
      rx.push(pa.left, pa.left + pa.width / 2, pa.left + pa.width);
      ry.push(pa.top, pa.top + pa.height / 2, pa.top + pa.height);
      cx.push(pa.left + pa.width / 2);
      cy.push(pa.top + pa.height / 2);
      /* 打印区也当作「等间距」的参照盒 —— 这样左右（上下）留白相等时会被吸住 */
      boxes.push({ left: pa.left, top: pa.top, width: pa.width, height: pa.height });
    }
    /* 2026-09-19: 参照物只保留打印区（见 SNAP_OTHERS 说明）——
       原先还把画布上每个对象的左/中/右 · 上/中/下 也当参照线。 */
    if (SNAP_OTHERS) {
      var inSel = (target && target.type === 'activeSelection' && target._objects) ? target._objects : null;
      var objs = c.getObjects();
      for (var i = 0; i < objs.length; i++) {
        var o = objs[i];
        if (o === target || o.isGrid || o.isPreview) continue;
        if (inSel && inSel.indexOf(o) >= 0) continue;
        var r = boxOf(o); if (!r) continue;
        rx.push(r.left, r.left + r.width / 2, r.left + r.width);
        ry.push(r.top, r.top + r.height / 2, r.top + r.height);
        cx.push(r.left + r.width / 2);
        cy.push(r.top + r.height / 2);
        boxes.push({ left: r.left, top: r.top, width: r.width, height: r.height });
      }
    }
    _refX = rx; _refY = ry; _refCx = cx; _refCy = cy; _refBoxes = boxes;
  }

  /* 等间距（#10）：两侧间隙相等时吸附，并画间距提示。
     只在该轴**没有被特征线吸住**时启用（特征线优先，避免两个吸附互相拉扯）。
     参照盒 = 打印区 + 其它对象；要求在该轴向有重叠（同一行 / 同一列）。 */
  function spacingFor(bb, tol, axis) {
    if (!_refBoxes || !_refBoxes.length) return null;
    var lo = (axis === 'y') ? bb.top : bb.left;
    var hi = lo + ((axis === 'y') ? bb.height : bb.width);
    var prev = null, next = null;
    for (var i = 0; i < _refBoxes.length; i++) {
      var r = _refBoxes[i];
      var rl = (axis === 'y') ? r.top : r.left;
      var rr = rl + ((axis === 'y') ? r.height : r.width);
      var p1 = (axis === 'y') ? bb.left : bb.top;
      var p2 = p1 + ((axis === 'y') ? bb.width : bb.height);
      var q1 = (axis === 'y') ? r.left : r.top;
      var q2 = q1 + ((axis === 'y') ? r.width : r.height);
      if (p2 <= q1 + 0.5 || q2 <= p1 + 0.5) continue;      // 另一轴不重叠 → 不是同一行/列
      if (rr <= lo + 0.5) { if (!prev || rr > prev.rr) prev = { rr: rr }; }
      else if (rl >= hi - 0.5) { if (!next || rl < next.rl) next = { rl: rl }; }
    }
    if (!prev || !next) return null;
    var g1 = lo - prev.rr, g2 = next.rl - hi;
    if (g1 < 0 || g2 < 0) return null;
    if (Math.abs(g1 - g2) > tol) return null;
    return { delta: (g2 - g1) / 2, gap: (g1 + g2) / 2, p1: prev.rr, p2: next.rl };
  }

  function onObjectMoving(e) {
    var c = CV(); if (!c || !e || !e.target) return;
    var t = e.target;
    _guides = [];
    _spacings = [];
    _moving = true;

    var bb = boxOf(t);
    if (!bb) { _moving = false; return; }
    /* 记「吸附前」的中心 —— 即用户的意图位置（见 _lastCx 声明处说明），仅松手磁吸用 */
    _lastCx = bb.left + bb.width / 2;
    _lastCy = bb.top + bb.height / 2;
    if (!_refX) buildRefs(t);

    var tol = guideTol();
    var bx = pickAxis([bb.left, bb.left + bb.width / 2, bb.left + bb.width], bb.left + bb.width / 2, _refX, _refCx, tol);
    var by = pickAxis([bb.top, bb.top + bb.height / 2, bb.top + bb.height], bb.top + bb.height / 2, _refY, _refCy, tol);

    if (bx || by) {
      try {
        t.set({ left: (t.left || 0) + (bx ? bx.delta : 0), top: (t.top || 0) + (by ? by.delta : 0) });
        if (t.setCoords) t.setCoords();
      } catch (er) { }
    }
    if (bx) _guides.push({ vertical: true, pos: bx.pos });
    if (by) _guides.push({ horizontal: true, pos: by.pos });

    /* 等间距：特征线优先（该轴已吸住就不再叠加，避免两个吸附互相拉扯） */
    var sx = bx ? null : spacingFor(bb, tol, 'x');
    var sy = by ? null : spacingFor(bb, tol, 'y');
    if (sx || sy) {
      try {
        t.set({ left: (t.left || 0) + (sx ? sx.delta : 0),
                top: (t.top || 0) + (sy ? sy.delta : 0) });
        if (t.setCoords) t.setCoords();
      } catch (e2) { }
      var fb = boxOf(t);
      if (fb) {
        if (sx) _spacings.push({ axis: 'x', a1: sx.p1, b1: sx.p1 + sx.gap,
                                 a2: sx.p2 - sx.gap, b2: sx.p2,
                                 at: fb.top + fb.height / 2, gap: sx.gap });
        if (sy) _spacings.push({ axis: 'y', a1: sy.p1, b1: sy.p1 + sy.gap,
                                 a2: sy.p2 - sy.gap, b2: sy.p2,
                                 at: fb.left + fb.width / 2, gap: sy.gap });
      }
    }
  }

  /* 叠加层绘制：智能参考线（仅拖动中，虚线洋红）。
     ⚠️ 批次4e 的「标尺 + 用户辅助线」已于 2026-09-15 按用户要求整条移除
        （原因：那条带数字的标尺看起来像坐标轴）。此处**不再**处理辅助线。 */
  function drawGuides() {
    var c = CV(); if (!c) return;
    if (!_moving || (!_guides.length && !_spacings.length)) return;
    try {
      var ctx = c.getContext();
      if (!ctx) return;

      /* 智能参考线（仅拖动中，虚线洋红） */
      var pa = printRect(); if (!pa) return;
      var vpt = c.viewportTransform || [1, 0, 0, 1, 0, 0];
      /* ⚠️ 线宽 / 虚线间隔必须按**显示比例**反算（`_pfmDisplayScale`），不能用 getZoom()：
         画布位图 3000² 而显示区仅 ~808²，这层缩放在 **CSS 上、不在 viewportTransform 里**
         → getZoom() 恒为 1 → 线宽 2「画布像素」→ 显示只有约 0.5px，几乎不可见
         （实测：参考线确实画上了，但细到看不出，还以为是打印区自带的中心十字）。
         同 drawGrid() 的做法 —— 那里也用 _pfmDisplayScale，目标视觉 3px。 */
      var sc = Math.max(0.001, c._pfmDisplayScale || 0.27);
      ctx.save();
      /* ⚠️ fabric 渲染完的上下文**不保证是默认状态**（可能残留 alpha / 混合模式），
         只设 strokeStyle 会被这些状态改写观感。故显式复位三个关键状态。 */
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = '#FF2D95';
      ctx.lineWidth = Math.max(1, Math.round(2 / sc));
      ctx.setLineDash([Math.max(2, Math.round(7 / sc)), Math.max(2, Math.round(4 / sc))]);
      for (var i = 0; i < _guides.length; i++) {
        var g = _guides[i], a, b;
        if (g.vertical) { a = { x: g.pos, y: pa.top }; b = { x: g.pos, y: pa.top + pa.height }; }
        else { a = { x: pa.left, y: g.pos }; b = { x: pa.left + pa.width, y: g.pos }; }
        var p1 = fabric.util.transformPoint(a, vpt);
        var p2 = fabric.util.transformPoint(b, vpt);
        ctx.beginPath();
        ctx.moveTo(p1.x, p1.y);
        ctx.lineTo(p2.x, p2.y);
        ctx.stroke();
      }

      /* 等间距提示：两段间隙线 + 数字标签（实线，与参考线的虚线区分开） */
      if (_spacings.length) {
        ctx.setLineDash([]);
        ctx.lineWidth = Math.max(1, Math.round(1.5 / sc));
        ctx.strokeStyle = '#FF2D95';
        var gfs = Math.max(10, Math.round(12 / sc));
        ctx.font = '600 ' + gfs + 'px system-ui,-apple-system,sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        for (var k = 0; k < _spacings.length; k++) {
          var sp = _spacings[k];
          var segs = (sp.axis === 'x')
            ? [[{ x: sp.a1, y: sp.at }, { x: sp.b1, y: sp.at }],
               [{ x: sp.a2, y: sp.at }, { x: sp.b2, y: sp.at }]]
            : [[{ x: sp.at, y: sp.a1 }, { x: sp.at, y: sp.b1 }],
               [{ x: sp.at, y: sp.a2 }, { x: sp.at, y: sp.b2 }]];
          for (var si = 0; si < 2; si++) {
            var t1 = fabric.util.transformPoint(segs[si][0], vpt);
            var t2 = fabric.util.transformPoint(segs[si][1], vpt);
            ctx.beginPath();
            ctx.moveTo(t1.x, t1.y); ctx.lineTo(t2.x, t2.y); ctx.stroke();
            var cap = Math.max(2, Math.round(4 / sc));
            ctx.beginPath();
            if (sp.axis === 'x') {
              ctx.moveTo(t1.x, t1.y - cap); ctx.lineTo(t1.x, t1.y + cap);
              ctx.moveTo(t2.x, t2.y - cap); ctx.lineTo(t2.x, t2.y + cap);
            } else {
              ctx.moveTo(t1.x - cap, t1.y); ctx.lineTo(t1.x + cap, t1.y);
              ctx.moveTo(t2.x - cap, t2.y); ctx.lineTo(t2.x + cap, t2.y);
            }
            ctx.stroke();
            /* 数字标签（先铺底色块保证在任意背景上可读）
               单位 = **屏幕px**：逻辑px × _pfmDisplayScale（🔴 是乘不是除 —— 一开始写成
               gap/sc，实测把 49px 显示成 672px，差 1/sc² 倍；同族换算见 drawGrid 的线宽） */
            var mx = (t1.x + t2.x) / 2, my = (t1.y + t2.y) / 2;
            var txt = String(Math.round(sp.gap * sc));
            var tw = ctx.measureText(txt).width;
            ctx.fillStyle = '#FF2D95';
            ctx.fillRect(mx - tw / 2 - gfs * 0.35, my - gfs * 0.62, tw + gfs * 0.7, gfs * 1.24);
            ctx.fillStyle = '#FFFFFF';
            ctx.fillText(txt, mx, my);
            ctx.fillStyle = '#FF2D95';
          }
        }
      }
      ctx.restore();
    } catch (e) { }
  }

  /* ---------------------------------------------------------------------------
     松手磁吸（2026-09-15 第二批 D）

     拖动中容差 10 屏幕px 要求用户拖得比较准；松手瞬间再给一个 **8 屏幕px** 的兜底
     窗口，越过容差但已经很接近时直接贴到**打印区中心**。
       · 只针对打印区中心 —— 不吸其它对象的特征线（避免松手后莫名其妙跳到别的对象上）
       · X / Y 轴独立判断（与拖动中吸附同一套语义）
       · 入撤销栈（走 D.save），用户可按 Undo 还原
     ⚠️ 必须给视觉反馈：否则用户会以为「我明明放这儿，它自己跳了」——
        故磁吸后把中心参考线闪 260ms。`_snapping` 期间要忽略后续 endMoving，
        否则 mouse:up 之后紧跟而来的 object:modified 会立刻把闪烁掐掉。
     --------------------------------------------------------------------------- */
  /* ⚠️ 2026-09-15 晚（第三批 A）：8 屏幕px **小于**手指拖动容差 10px ⇒ 磁吸条件被拖动容差
     完全包含 ⇒ `snapToCenter()` 不再产生任何额外效果（等于顺手取消了 D）。
     想把「轻微磁吸」要回来，把这里改成 **14**（必须 > 手指容差 10）即可。 */
  var SNAP_SCREEN = 8;      // 松手磁吸窗口（**屏幕**px）
  var _snapping = false;    // 正在闪参考线 → 期间忽略 endMoving
  var _snapFlash = null;

  function snapTol() {
    var c = CV();
    var sc = Math.max(0.001, (c && c._pfmDisplayScale) || 1);
    return SNAP_SCREEN / sc;                  // → 画布逻辑px
  }

  function snapToCenter() {
    var c = CV(); if (!c) return false;
    var pa = printRect(); if (!pa) return false;
    var t = null;
    try { t = c.getActiveObject(); } catch (e) { }
    if (!t) return false;
    var bb = boxOf(t); if (!bb) return false;

    var cx = pa.left + pa.width / 2, cy = pa.top + pa.height / 2;
    var tol = snapTol();
    /* 判断用**意图位置**（拖动最后一帧、吸附前）；移动量用**当前**中心（对象实际在哪就搬哪）。
       ⚠️ 拿当前中心判断会漏：边缘吸附已把中心推到「半宽」处，宽对象直接超出 30px 窗口
          （平板实测 33.5 屏幕px > 30）⇒ 磁吸永远不触发。 */
    var ix = (_lastCx === null) ? (bb.left + bb.width / 2) : _lastCx;
    var iy = (_lastCy === null) ? (bb.top + bb.height / 2) : _lastCy;
    _lastCx = null; _lastCy = null;
    var dx = cx - (bb.left + bb.width / 2);
    var dy = cy - (bb.top + bb.height / 2);
    var mx = Math.abs(cx - ix) <= tol ? dx : 0;
    var my = Math.abs(cy - iy) <= tol ? dy : 0;
    if (!mx && !my) return false;

    moveBy(t, mx, my);
    try { if (t.setCoords) t.setCoords(); } catch (e) { }

    _guides = [];
    if (mx) _guides.push({ vertical: true, pos: cx });
    if (my) _guides.push({ horizontal: true, pos: cy });
    _moving = true;
    _snapping = true;
    try { c.requestRenderAll(); } catch (e) { }
    clearTimeout(_snapFlash);
    _snapFlash = setTimeout(function () {
      _snapping = false;
      _moving = false;
      _guides = [];
      var cc = CV();
      try { if (cc && cc.requestRenderAll) cc.requestRenderAll(); } catch (e) { }
    }, 260);
    try { if (D && D.save) D.save(); } catch (e) { }
    return true;
  }

  function beginMoving() {
    _refX = null; _refY = null; _refCx = null; _refCy = null; _refBoxes = null;
    _lastCx = null; _lastCy = null;          // 上一轮的意图位置不能留到本轮
    /* 用户再次按下拖动 ⇒ 立刻结束上一次的磁吸闪烁（否则 _snapping 会挡住本次 endMoving） */
    _snapping = false;
    clearTimeout(_snapFlash);
  }

  function endMoving() {
    if (_snapping) return;                    // 闪烁反馈期间忽略 mouse:up / object:modified
    if (!_moving) { _refX = null; _refY = null; _refCx = null; _refCy = null; return; }
    _moving = false;
    _guides = [];
    _spacings = [];
    _refX = null; _refY = null; _refCx = null; _refCy = null;
    snapToCenter();                           // 松手兜底：≤8 屏幕px → 贴打印区中心（见 SNAP_SCREEN 说明）
    var c = CV();
    try { if (c && c.requestRenderAll) c.requestRenderAll(); } catch (e) { }
  }

  /* ---------------------------------------------------------------------------
     方向键微调
     —— 只挂一个 document 级 keydown；board 未打开 / 焦点在输入框内 / 带修饰键时全部放行，
        不干扰既有行为。fabric 不消费方向键，故安全。
     --------------------------------------------------------------------------- */
  function boardOpen() {
    var b = q('pfmBoard');
    if (!b) return false;
    var r = b.getBoundingClientRect();
    return r.width > 2 && r.height > 2;
  }

  function onKeyDown(e) {
    try {
      if (!boardOpen()) return;
      var t = e.target;
      if (t && t.tagName && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      var step = e.shiftKey ? 10 : 1, dx = 0, dy = 0, k = e.key;
      if (k === 'ArrowLeft') { dx = -step; }
      else if (k === 'ArrowRight') { dx = step; }
      else if (k === 'ArrowUp') { dy = -step; }
      else if (k === 'ArrowDown') { dy = step; }
      else { return; }

      var objs = selObjs();
      if (!objs.length) return;
      e.preventDefault();
      objs.forEach(function (o) { moveBy(o, dx, dy); });
      commit();
    } catch (er) { }
  }


  /* ---------------------------------------------------------------------------
     事件绑定 —— 必须「可补绑」。
     ⚠️ init 由 updateLayers() 每轮调用，而**首次**调用发生在 hd-align.js（defer）
     加载完成时，此刻主 IIFE 的 canvas 尚未创建 → 若把 c.on(...) 只写在 init 的
     首次分支里，事件会**永不绑定**（表现为「拖动时完全没有参考线」）。
     故改为此处幂等补绑：任意一次调用时 canvas 就绪即绑上。
     --------------------------------------------------------------------------- */
  function bindCanvasEvents() {
    if (bound) return;
    var c = CV();
    if (!c || !c.on) return;
    bound = true;
    try {
      /* 智能参考线 + 吸附（批次4b）—— 拖动中实时对齐，并在画布上画参考线
         ⚠️ 对齐面板已移除 ⇒ 不再需要 selection:* 的同步绑定（原来只为刷面板状态）。 */
      c.on('mouse:down', beginMoving);
      c.on('object:moving', onObjectMoving);
      c.on('after:render', drawGuides);
      c.on('mouse:up', endMoving);
      c.on('object:modified', endMoving);   // 触摸端 mouse:up 不一定触发，兜底
      c.on('selection:cleared', endMoving);
    } catch (e) { bound = false; }
  }

  function bindKeys() {
    if (keyBound) return;
    keyBound = true;
    try { document.addEventListener('keydown', onKeyDown); } catch (e) { keyBound = false; }
  }

  /* ---------------------------------------------------------------------------
     init（幂等 —— updateLayers() 每轮都会调用）
     --------------------------------------------------------------------------- */
  function init(deps) {
    if (ready) {
      bindCanvasEvents();          // 补绑（首次可能 canvas 未就绪）
      bindKeys();
      return;
    }
    ready = true;
    D = deps || {};
    /* 批次4e 辅助线已移除 —— 清掉历史 localStorage 残留，避免旧数据留在用户设备上 */
    try { localStorage.removeItem('pfmGuides'); } catch (e) { }
    bindCanvasEvents();
    bindKeys();
  }

  window.__hdAlign = {
    init: init,
    /* 智能参考线 —— 状态读取，供真机测试断言（对齐面板已于 2026-09-15 移除） */
    guides: function () { return _guides.slice(); },
    isMoving: function () { return _moving; },
    guideTol: guideTol,           // **画布逻辑px** —— 拖动中实时吸附的容差
    guideTolScreen: tolScreen,    // 设计意图的**屏幕px**（鼠标 2.4 / 手指 1.2）
    snapTolScreen: function () { return SNAP_SCREEN; },   // 松手磁吸窗口（屏幕px）
    snapTol: snapTol,                                     // 松手磁吸窗口（画布逻辑px）
    snapToCenter: snapToCenter                            // 直接驱动（供真机测试）
  };

  /* defer 脚本的执行时机**晚于**内联主脚本 —— 此刻主 IIFE 已运行完毕，
     故主动拉取一次依赖注入。否则要等用户首次改动对象触发 updateLayers()，
     才会走到 hdAlignBridge() → 表现为「打开设计板但面板空白」。 */
  try { if (window.__pfmAlignBoot) window.__pfmAlignBoot(); } catch (e) { }
})();
